import type { ReactNode } from 'react';
import { css, styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { H3 } from '#/components/Text/H3';
import { HeadingSmall } from '#/components/Text/HeadingSmall';
import { Tile } from '#/components/Tile/Tile';
import { TileBody } from '#/components/Tile/TileBody';
import { colors } from '#/theme/theme';

const StyledTile = styled(Tile)<{ $glowUp?: boolean }>`
  &[id] {
    scroll-margin-top: calc(83px + 36px);
  }

  margin-bottom: 36px;

  &:not(:first-child) {
    margin: 36px 0;
  }

  ${({ $glowUp }: { $glowUp?: boolean }) =>
    $glowUp &&
    css`
      background:
        linear-gradient(${colors.white}, ${colors.white}) padding-box,
        linear-gradient(
            285deg,
            ${colors.brandYellow},
            ${colors.brandGreen},
            ${colors.brandBlue}
          )
          border-box;
      border: 2px solid transparent;
      border-radius: 4px;
    `}
`;
const StyledTileBody = styled(TileBody)`
  & > *:last-child {
    margin-bottom: 0;
  }
`;

const DisabledTile = styled(StyledTile)`
  background-color: ${colors.backgroundGrey};
  opacity: 0.6;
  pointer-events: none;
`;

const DescriptionVariants = {
  large: css`
    margin-block-end: 56px;
  `,
  compact: css`
    margin-block-end: 1em;
  `,
};

const Description = styled.div<{
  variant: keyof typeof DescriptionVariants | undefined;
}>`
  ${(props) => {
    if (!props.variant) return;
    return DescriptionVariants[props.variant];
  }}
`;

type Props = {
  id?: string;
  className?: string;
  preTitle?: string;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  large?: boolean;
  bodyPaddingBottom?: (string | number | null)[];
  compactDescription?: boolean;
  qaId?: string;
  disabled?: boolean;
  beforeSection?: ReactNode;
  glowUp?: boolean;
};

export function FormSection({
  id,
  className,
  preTitle,
  title,
  description,
  children,
  large,
  compactDescription,
  bodyPaddingBottom,
  qaId,
  disabled,
  beforeSection,
  glowUp,
}: Props) {
  let descriptionVariant: keyof typeof DescriptionVariants | undefined;
  if (large) descriptionVariant = 'large';
  if (compactDescription) descriptionVariant = 'compact';

  const StyledTileComponent = disabled ? DisabledTile : StyledTile;

  return (
    <>
      {beforeSection && (
        <div style={{ marginBottom: '24px' }}>{beforeSection}</div>
      )}
      <StyledTileComponent
        id={id}
        className={className}
        qaId={qaId}
        $glowUp={glowUp}
      >
        <StyledTileBody
          p={large ? [36, null, null, 96] : [20, null, null, 40]}
          pt={large ? [36, null, 64] : undefined}
          pb={bodyPaddingBottom}
        >
          {preTitle && (
            <Box mb="-16px">
              <HeadingSmall>{preTitle}</HeadingSmall>
            </Box>
          )}

          {title && (
            <H3 mb={large && !description ? 56 : undefined}>{title}</H3>
          )}

          {description && (
            <Description variant={descriptionVariant}>
              {' '}
              {description}
            </Description>
          )}

          {children}
        </StyledTileBody>
      </StyledTileComponent>
    </>
  );
}
